import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    icon: 'ri-meta-line',
    title: 'Facebook & Instagram Ads',
    description:
      'We build and manage full-scale paid social campaigns — relentlessly tested, ruthlessly optimized, engineered to convert at the lowest possible cost per acquisition. Every dollar works harder.',
    tags: ['Meta Ads', 'Creative Strategy', 'A/B Testing', 'Retargeting'],
  },
  {
    number: '02',
    icon: 'ri-brain-line',
    title: 'Proprietary AI Audience Algorithm',
    description:
      'Our secret sauce. Trained on 100M+ data points, our AI identifies and locks onto your most profitable buyer personas before your competition even knows they exist. It learns. It adapts. It wins.',
    tags: ['Lookalike 2.0', 'Predictive Bidding', 'Intent Signals', 'AI Targeting'],
    highlight: true,
  },
  {
    number: '03',
    icon: 'ri-line-chart-line',
    title: 'Full-Funnel Performance Marketing',
    description:
      'From first impression to closed deal — we own the entire funnel. Landing pages, follow-up sequences, lead nurturing, conversion rate optimization. Nothing falls through the cracks.',
    tags: ['CRO', 'Lead Nurturing', 'Funnel Builds', 'Analytics'],
  },
];

export default function Process() {
  const ref = useScrollAnimation();

  return (
    <section id="services" className="bg-black py-24 md:py-32 border-t border-white/5">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-20 fade-in-up">
          <p className="text-electric-blue text-xs font-semibold tracking-widest uppercase mb-4">
            The CGOeight System
          </p>
          <h2 className="font-display font-bold text-white leading-tight"
            style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}
          >
            How We 3X Your Profit.
          </h2>
          <p className="text-white/40 text-base md:text-lg mt-4 max-w-xl mx-auto leading-relaxed">
            The CGOeight System — three pillars of AI-powered performance marketing. Built for businesses that refuse to settle for average.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`fade-in-up group relative rounded-lg p-8 md:p-10 border transition-all duration-500 ${
                step.highlight
                  ? 'bg-electric-blue/5 border-electric-blue/25 hover:border-electric-blue/50'
                  : 'bg-dark-card border-dark-border hover:border-white/10'
              }`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              {/* Highlight label */}
              {step.highlight && (
                <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-electric-blue/10 border border-electric-blue/30">
                  <span className="text-electric-blue text-xs font-bold tracking-wider uppercase">Our Secret Sauce</span>
                </div>
              )}

              {/* Number */}
              <span className="font-display font-bold text-5xl md:text-6xl leading-none mb-6 block"
                style={{ color: step.highlight ? 'rgba(0,240,255,0.15)' : 'rgba(255,255,255,0.05)' }}
              >
                {step.number}
              </span>

              {/* Title */}
              <h3 className="font-display font-bold text-white text-xl mb-4 leading-tight">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-white/45 text-sm leading-relaxed mb-6">
                {step.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {step.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                      step.highlight
                        ? 'border-electric-blue/20 text-electric-blue/70 bg-electric-blue/5'
                        : 'border-white/10 text-white/30 bg-white/3'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
